import React, { useEffect } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { QuickAction } from '../shared';
import { CHAT_TEXT } from '../../../constants/chat';

interface ChatInputAreaProps {
  inputMessage: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  isLoading: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  adjustTextareaHeight: (textarea: HTMLTextAreaElement | null) => void;
  placeholder?: string;
  quickActions?: QuickAction[];
  variant?: 'panel' | 'embedded';
}

/**
 * Chat input area with textarea, send button, and optional quick actions
 * Used in both sliding panel and embedded modes
 */
export const ChatInputArea: React.FC<ChatInputAreaProps> = ({
  inputMessage,
  onInputChange,
  onSend,
  onKeyPress,
  isLoading,
  inputRef,
  adjustTextareaHeight,
  placeholder = CHAT_TEXT.PLACEHOLDER,
  quickActions = [],
  variant = 'panel',
}) => {
  // Auto-resize textarea on input change
  useEffect(() => {
    adjustTextareaHeight(inputRef.current);
  }, [inputMessage, adjustTextareaHeight, inputRef]);

  // Embedded mode - matches original layout with quick action row + embedded send button
  // Fixed at bottom on mobile (ChatGPT-style), relative on desktop
  if (variant === 'embedded') {
    return (
      <div
        data-tour="chat-input-area"
        className="px-4 py-3 bg-white/95 backdrop-blur-sm flex-shrink-0 border-t border-gray-200 fixed bottom-0 left-0 right-0 z-20 lg:relative lg:bottom-auto lg:left-auto lg:right-auto lg:bg-white lg:backdrop-blur-none"
        style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
      >
        {/* Quick Action Pills - always show in embedded mode */}
        {quickActions.length > 0 && (
          <div data-tour="workflow-buttons" className="flex gap-1.5 mb-3">
            {quickActions.map((action, idx) => (
              <button
                key={idx}
                onClick={action.action}
                className="group flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-medium text-gray-500 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:text-gray-700 hover:bg-gray-50 hover:shadow-sm transition-all"
              >
                <action.icon className="w-3 h-3 text-gray-400 group-hover:text-gray-500 transition-colors flex-shrink-0" />
                <span className="truncate">{action.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Input with embedded send button */}
        <div className="relative bg-gray-50 border border-gray-200 rounded-2xl focus-within:border-gray-300 focus-within:bg-white focus-within:shadow-sm transition-all">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => {
              onInputChange(e.target.value);
              adjustTextareaHeight(e.target);
            }}
            onKeyDown={onKeyPress}
            placeholder={placeholder}
            className="w-full px-4 py-3 pr-12 text-[14px] bg-transparent rounded-2xl outline-none resize-none placeholder:text-gray-400"
            style={{ minHeight: '44px', maxHeight: '140px', lineHeight: '1.5' }}
            disabled={isLoading}
          />
          <button
            onClick={onSend}
            disabled={!inputMessage.trim() || isLoading}
            aria-label={isLoading ? 'Sending message' : 'Send message'}
            className="absolute right-2 bottom-2 w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center transition-all hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Send className="w-4 h-4 text-white" />
            )}
          </button>
        </div>
      </div>
    );
  }

  // Panel mode - simple layout with separate send button
  return (
    <div className="p-4 border-t border-gray-100 bg-white">
      <div className="flex gap-2 items-end">
        <textarea
          ref={inputRef}
          value={inputMessage}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={onKeyPress}
          placeholder={placeholder}
          className="flex-1 px-4 py-3 text-[14px] bg-gray-50 border border-gray-200 rounded-xl outline-none resize-none placeholder:text-gray-400 focus:border-gray-300 focus:bg-white transition-colors"
          style={{ minHeight: '44px', maxHeight: '120px', lineHeight: '1.5' }}
          disabled={isLoading}
        />
        <button
          onClick={onSend}
          disabled={!inputMessage.trim() || isLoading}
          aria-label={isLoading ? 'Sending message' : 'Send message'}
          className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center transition-all hover:bg-gray-800 disabled:bg-gray-200 disabled:cursor-not-allowed flex-shrink-0"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 text-white animate-spin" />
          ) : (
            <Send className="w-4 h-4 text-white" />
          )}
        </button>
      </div>
    </div>
  );
};
